import { normalizeOrganizationId } from '../helper/organization-id';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ApService {
  private deviceSipWatchCache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) { }
  getListing(data): Observable<any> {
    data = this.normalizeListingOptions(data);
    return this.http.get<any>(environment.endpoints.ap, data);
  }

  generateRequestedColumns(data): Observable<any> {
    if (data && ((Array.isArray(data.devices) && data.devices.length) || data.filters || data.registration)) {
      return this.http.post<any>(environment.endpoints.ap + '/requested-columns', data);
    }

    const params: any = {
      orgId: data && data.orgId ? data.orgId : '',
      columns: JSON.stringify(data && data.columns ? data.columns : [])
    };

    if (data && data.limit) {
      params.limit = data.limit;
    }

    return this.http.get<any>(environment.endpoints.ap + '/requested-columns', { params });
  }

  private normalizeListingOptions(data) {
    const shouldBoundRequest = data?.params?.type === 'overlay' ||
      String(data?.params?.searchTxt || '').trim().length > 0;

    if (shouldBoundRequest) {
      data = {
        ...data,
        params: {
          ...data.params,
          // Search and overlay requests must always be bounded, including future callers.
          page: data.params.page || 1,
          pageSize: data.params.pageSize || 50
        }
      };
    }

    if (!data || !data.params || !data.params.id || typeof data.params.id !== 'object') {
      return data;
    }

    return {
      ...data,
      params: {
        ...data.params,
        id: this.normalizeId(data.params.id)
      }
    };
  }

  private normalizeId(id, depth = 0) {
    return normalizeOrganizationId(id, depth);
  }

  getPendingDevice(data): Observable<any> {
    data = this.normalizePendingDeviceOptions(data);
    return this.http.get<any>(environment.endpoints.getpendingdevicesdata, data)
  }

  private normalizePendingDeviceOptions(data) {
    if (!data || !data.params || typeof data.params.orgId !== 'object') {
      return data;
    }

    return {
      ...data,
      params: {
        ...data.params,
        orgId: this.normalizeId(data.params.orgId)
      }
    };
  }

  movePendingDevices(data): Observable<any> {
    return this.http.post<any>(environment.endpoints.postdevice, data)
  }

  getdeviceById(id): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/' + id)
  }

  getdeviceByMonth(id, startDate, endDate): Observable<any> {
    id = this.normalizeId(id);
    if (!id) return throwError(new Error('Invalid organization ID.'));
    return this.http.get<any>(environment.endpoints.ap + '/search?orgId=' + id + '&start_date=' + startDate + '&end_date=' + endDate)
  }

  getDevicesByOrg(id): Observable<any> {
    id = this.normalizeId(id);
    if (!id) return throwError(new Error('Invalid organization ID.'));
    return this.http.get<any>(environment.endpoints.ap + '/search?orgId=' + id)
  }

  getHistoryDataById(id, data): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/history-data/' + id, data)
  }

  getDevicesHistoryDataById(id, data): Observable<any> {    
    return this.http.get<any>(environment.endpoints.deviceHistoryData + '?deviceId=' + id,  { params: data } )
  }

  posdevicedata(data): Observable<any> {
    return this.http.post<any>(environment.endpoints.postdevice, data)
  }

  updateDevice(data): Observable<any> {
    return this.http.post<any>(environment.endpoints.updateDevice, data)
  }

  deviceDelete(data) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: {
        device: data
      }
    };
    return this.http.delete<any>(environment.endpoints.devicedelete, options);
  }

  getbackup(id): Observable<any> {
    return this.http.get<any>(environment.endpoints.deviceBackup + '/' + id + '/' + 'backup')
  }

  createBackup(data, id): Observable<any> {
    return this.http.post<any>(environment.endpoints.deviceBackup + '/' + id + '/setCommand', data)
  }

  deleteBackup(data, id): Observable<any> {
    return this.http.delete<any>(environment.endpoints.deviceBackup + '/' + id + '/backup/' + data);
  }

  getPrtgDevices(data, prtgId): Observable<any> {
    return this.http.get<any>(environment.endpoints.getPrtgDevice + '/prtg/' + prtgId + '/templateDevices', data)
  }

  getWnetworkDevices(data, wnetworkId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/wnetwork/' + wnetworkId + '/templateDevices', data)
  }

  getQosDevices(data, qosId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/qos/' + qosId + '/templateDevices', data)
  }

  getI4glteDevices(data, i4glteId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/i4glte/' + i4glteId + '/templateDevices', data)
  }

  getTeamDevices(data, teamId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/team/' + teamId + '/templateDevices', data)
  }

  getTprofileDevices(data, tprofileId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/tprofile/' + tprofileId + '/templateDevices', data)
  }

  getAlertDevices(data, alertId): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/alert/' + alertId + '/templateDevices', data)
  }

  setDeviceCommand(deviceId, data): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/' + deviceId + '/setCommand', data)
  }

  executeDeviceShellOperations(
    deviceMac: string,
    commands: string[],
    executionMode: 'auto' | 'sync' | 'async' = 'auto'
  ): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/shell-operations/execute',
      { commands, execution_mode: executionMode }
    )
  }

  getDeviceShellOperation(deviceMac: string, operationId: string): Observable<any> {
    return this.http.get<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) +
      '/shell-operations/' + encodeURIComponent(operationId)
    )
  }

  getDeviceSupportTools(deviceMac: string): Observable<any> {
    return this.http.get<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/support-tools'
    ).pipe(timeout(30000))
  }

  previewDeviceSupportTools(deviceMac: string, payload: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/support-tools/preview',
      payload
    ).pipe(timeout(30000))
  }

  executeDeviceSupportTools(deviceMac: string, payload: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/support-tools/execute',
      payload
    )
  }

  getDeviceSipWatchList(deviceMac: string, forceRefresh = false): Observable<any> {
    const cacheKey = String(deviceMac || '').toLowerCase();
    if (forceRefresh) this.deviceSipWatchCache.delete(cacheKey);

    if (!this.deviceSipWatchCache.has(cacheKey)) {
      const request = this.http.get<any>(
        environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/sipwatch/watch-list'
      ).pipe(
        timeout(30000),
        shareReplay(1),
        catchError(error => {
          this.deviceSipWatchCache.delete(cacheKey);
          return throwError(error);
        })
      );
      this.deviceSipWatchCache.set(cacheKey, request);
    }

    return this.deviceSipWatchCache.get(cacheKey);
  }

  mutateDeviceSipWatchList(deviceMac: string, payload: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/' + encodeURIComponent(deviceMac) + '/sipwatch/watch-list',
      payload
    ).pipe(
      timeout(30000),
      tap(() => this.deviceSipWatchCache.delete(String(deviceMac || '').toLowerCase()))
    )
  }

  toggleTemplate(deviceId, template, value): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/' + deviceId + '/templateConfig', {
      template: template, value: value, _id: deviceId
    });
  }

  getLicenseHistoryByMonth(orgId, startDate, endDate): Observable<any> {
    orgId = this.normalizeId(orgId);
    if (!orgId) return throwError(new Error('Invalid organization ID.'));
    return this.http.get<any>(environment.endpoints.ap + '/license-audit/history?orgId=' + orgId + '&startDate=' + startDate + '&endDate=' + endDate)
  }

  getDeviceOrganizations(macs): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/device-organizations', { macs })
  }

  createDeviceBrowseHistory(mac, type): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/history/device-browse-history', { mac, type })
  }

  getDeviceBrowseHistory(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/history/device-browse-history', data)
  }

  getLanData(data): Observable<any> {
    return this.http.post<any>(environment.endpoints.lan, data)
  }

  getDeviceState(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.trackDeviceStatus, { params: data })
  }

  getLinkState(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.trackLinkStatus, { params: data })
  }

  getDeviceCalc(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.trackDeviceCalc, { params: data })
  }

  getLinkCalc(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.trackLinkCalc, { params: data })
  }

  getAttackShieldHist(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.attackShield + '/history', { params: data })
  }

  getLinkStatesHist(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.linkStateHist + '/history', { params: data })
  }

  getSipwatchAlerts(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.sipwatchAlert, { params: data })
  }

  getSipwatchCallerIdHist(data): Observable<any> {
    return this.http.get<any>(environment.endpoints.sipwatchCallerIdHist, { params: data })
  }
  
  set4glteAlertByOrg(data): Observable<any> {
    return this.http.post<any>(environment.endpoints.set4glteAlertByOrg, data)
  }

  getAuthlogs(deviceId,data): Observable<any> {
    return this.http.get<any>(`${environment.endpoints.ap}/authlogs/${deviceId}`, { params: data })
  }

  getAllAlerts(deviceId,data): Observable<any> {
    return this.http.get<any>(`${environment.endpoints.alerts}/combine_alerts/${deviceId}`, { params: data })
  }
}
